import { getBooleanFromValue, isFunction, isObject, mergeDeep } from '@/global/utils/utils'

const DEFAULT_PAGINATION_CONFIG = {
  perPageOptions: [50, 100],
  perPage: 50,
  currentPage: 1
}
const DEFAULT_TABLE_STYLE = {}

/**
 * Colour a row wears once the user has picked it out. One listing marks its rows to
 * feed a map beside it and another ticks them for an action to be applied to - the
 * same act, so the same colour and, underneath, the same state. The grey a row wears
 * under the cursor - see `.row--interactive:hover` in TableBody.
 */
export const DEFAULT_SELECTED_ROW_COLOR = '#F3F4F6'

/**
 * How long a screen that acts on the selection may be made to wait for it.
 *
 * Picking rows usually drives a fetch, and a fetch puts a loader over the very rows
 * the user is still clicking - so a listing whose selection costs a request has the
 * run of clicks let finish first, and asks once, for all of them. The rows themselves
 * are ticked as they are clicked either way; only the callback waits. Set per table
 * with `rowsSelection.changeDelay`.
 */
export const DEFAULT_SELECTION_CHANGE_DELAY = 400

/**
 * Properties a row is identified by when the screen does not name one with
 * `rememberSelectionByDataProperty`. A stable key is what lets a tick survive a
 * refetch and a walk to another screen and back - an index only means anything
 * while the very same page stays on screen.
 */
export const DEFAULT_SELECTION_KEY_PROPERTIES = ['id', 'uuid']

/**
 * Ticking rows, with the app-wide behaviour filled in.
 *
 * One click on a row ticks or unticks it, and a double click opens the record -
 * everywhere in the application. A listing without the checkbox column holds one
 * ticked row at a time, and one with it holds as many as the user ticks - see
 * `isMultipleRowsSelection`. A screen only says what is its own: whether the checkbox
 * column is shown, what a change should do, and, where the default is not what it
 * wants, how many rows may be ticked at once (`multiple`).
 */
export const DEFAULT_ROWS_SELECTION_CONFIG = {
  // Whether the checkbox column is drawn. Rows are still ticked by clicking them
  // when it is not - the tick then shows as the row's colour.
  showCheckboxes: false,
  // Whether several rows may be ticked at once. Left unset it follows the checkbox
  // column - see `isMultipleRowsSelection`.
  multiple: undefined,
  // A plain click on the row ticks it. Turned off only by a listing whose click has
  // to mean something else entirely.
  selectOnRowClick: true,
  resetSelections: false,
  // Which property identifies a row across refetches; empty falls back to
  // `DEFAULT_SELECTION_KEY_PROPERTIES` and then to the row index.
  rememberSelectionByDataProperty: '',
  // Whether ticks survive a refetch and navigating between screens. They do - the
  // user only loses them by refreshing the page. A listing whose ticks stand for the
  // page in front of the user rather than for records turns this off.
  keepSelection: true,
  // Colour a ticked row wears.
  selectedColor: DEFAULT_SELECTED_ROW_COLOR,
  // Called with { selectionState, data, index } as each row is ticked.
  rowSelect: null,
  // Called with { data } - every ticked row - after each change.
  selectionsChange: null,
  // Milliseconds `selectionsChange` waits after the last click, for a listing whose
  // selection costs a request - see `DEFAULT_SELECTION_CHANGE_DELAY`. 0 reports every
  // click as it happens, which is what a screen that only reads the rows wants.
  changeDelay: 0,
  // Called with { selectionState } when the header checkbox settles the whole page.
  onSelectionAll: null
}

/**
 * The key a row is remembered by, the same way everywhere a row can be ticked - the
 * table body, the grid card and the table itself - so none of them can disagree about
 * which record a tick belongs to.
 *
 * @param {object} rowData
 * @param {number} rowIndex
 * @param {object} [selectionConfig] the screen's `rowsSelection`
 * @return {string|number|undefined}
 */
export function getRowSelectionKeyFor (rowData, rowIndex, selectionConfig = {}) {
  const { rememberSelectionByDataProperty } = selectionConfig || {}

  if (rememberSelectionByDataProperty && rowData?.[rememberSelectionByDataProperty] !== undefined) {
    return rowData[rememberSelectionByDataProperty]
  }

  if (!rememberSelectionByDataProperty && rowData) {
    const property = DEFAULT_SELECTION_KEY_PROPERTIES.find(key => rowData[key] !== undefined && rowData[key] !== null)

    if (property) {
      return rowData[property]
    }
  }

  return rowIndex
}

/**
 * Whether a row is keyed by something of its own rather than by where it happens to
 * sit. Only such a tick is worth keeping across a refetch or a screen change.
 *
 * @param {object} rowData
 * @param {object} [selectionConfig]
 * @return {boolean}
 */
export function hasStableSelectionKey (rowData, selectionConfig = {}) {
  const { rememberSelectionByDataProperty } = selectionConfig || {}

  if (rememberSelectionByDataProperty) {
    return rowData?.[rememberSelectionByDataProperty] !== undefined
  }

  return DEFAULT_SELECTION_KEY_PROPERTIES.some(key => rowData?.[key] !== undefined && rowData?.[key] !== null)
}

/**
 * Whether several rows of a listing may be ticked at once.
 *
 * The screen's own `multiple` where it set one. Otherwise it follows the checkbox
 * column: a column of boxes says rows are picked out several at a time, and without
 * it a click on a row marks that row alone - the one marked before lets go.
 *
 * A table whose rows open up under themselves is the exception: an open row is a
 * ticked row (see `syncSelectionWithExpansion` in TableBody), and several of them may
 * be open at once - so several are ticked.
 *
 * @param {object} selectionConfig the table's `rowsSelection`
 * @param {object} [tableConfig] the table's `tableConfig`, for its `rowExpansion`
 * @return {boolean}
 */
export function isMultipleRowsSelection (selectionConfig, tableConfig = null) {
  const { multiple, showCheckboxes } = isObject(selectionConfig) ? selectionConfig : {}

  if (multiple !== undefined && multiple !== null) {
    return getBooleanFromValue(multiple)
  }

  if (isFunction(tableConfig?.rowExpansion?.isExpandable)) {
    return true
  }

  return getBooleanFromValue(showCheckboxes)
}

/**
 * What a row of this table does when the user reaches for it - read off the same
 * config the rows act on, so what the table says it does and what it does cannot
 * drift apart. Drawn as the row's cursor and as the hints under the table.
 *
 * - `selectsOnClick`: a click ticks the row - see `rowsSelection.selectOnRowClick`
 * - `expandsOnClick`: a click opens more of the row under it - `tableConfig.rowExpansion`
 * - `opensOnDoubleClick`: a double click opens the record - see `getOpenRecordHandler`
 *
 * @param {object} config the table's config
 * @return {{ selectsOnClick: boolean, expandsOnClick: boolean, opensOnDoubleClick: boolean }}
 */
export function getTableRowInteractions (config) {
  const { rowsSelection } = config || {}
  const selectionConfig = {
    ...DEFAULT_ROWS_SELECTION_CONFIG,
    ...(isObject(rowsSelection) ? rowsSelection : {})
  }
  const { isExpandable } = config?.tableConfig?.rowExpansion || {}

  return {
    selectsOnClick: getBooleanFromValue(selectionConfig.selectOnRowClick),
    expandsOnClick: isFunction(isExpandable),
    opensOnDoubleClick: !!getOpenRecordHandler(config)
  }
}

/**
 * What opens the record a row or a card stands for: the screen's `rowDblClick`.
 *
 * @param {object} config the table's config
 * @return {Function|null}
 */
export function getOpenRecordHandler (config) {
  const { rowDblClick } = config || {}

  return isFunction(rowDblClick) ? rowDblClick : null
}

/**
 * Ready-made signs of what a click on a row does, for `rowClickIndicator` to name. The
 * tooltip is a translation key, read by the table.
 */
export const ROW_CLICK_INDICATORS = Object.freeze({
  // A click on the row shows it on the map beside the table.
  map: Object.freeze({
    icon: 'mdi-map-marker-outline',
    tooltipKey: 'base.table_row_click_shows_on_map'
  })
})

/**
 * The icon a row carries at its left edge to say what a click on it does - for a
 * screen where the click does something the user would not guess, such as showing the
 * row on a map beside the table. Shown under the cursor and on a ticked row.
 *
 * `rowClickIndicator` is the name of one of `ROW_CLICK_INDICATORS` (`'map'`), or a
 * screen's own `{ icon, tooltip }`.
 *
 * @param {object} config the table's config
 * @return {{ icon: string, tooltip?: string, tooltipKey?: string }|null}
 */
export function getRowClickIndicator (config) {
  const { rowClickIndicator } = config || {}

  if (typeof rowClickIndicator === 'string') {
    return ROW_CLICK_INDICATORS[rowClickIndicator] || null
  }

  return isObject(rowClickIndicator) && rowClickIndicator.icon ? rowClickIndicator : null
}

/**
 * Whether the rows get a column of their own controls at their left edge - the arrow
 * of a row that opens up, and the screen's `rowClickIndicator`. The
 * header, the rows and the summary all ask this, so they agree on the column count.
 *
 * @param {object} config the table's config
 * @return {boolean}
 */
export function hasLeadingRowControls (config) {
  return getTableRowInteractions(config).expandsOnClick || !!getRowClickIndicator(config)
}

/**
 * Import this when you want the full config shape as a reference while wiring a table.
 */
export const TABLE_CONFIG_PLACEHOLDERS = {
  uiView: '',
  key: '',
  title: '',
  showLoader: false,
  callFetchData: false,
  fetchOnCreate: true,
  rememberTableLayout: true,
  visible: undefined,
  permissions: null,
  invalidateSize: false,
  tableData: undefined,
  summaryData: undefined,
  openDetail: null,
  updateHeaders: [],
  internalPagination: {
    enable: false
  },
  title_and_back_config: {
    title: '',
    showBackButton: {
      uiViewName: ''
    }
  },
  showBackButton: {
    uiViewName: ''
  },
  search_and_buttons_config: {
    search_field_config: null,
    additional_buttons: [],
    newClick: null,
    showNewBtn: true,
    disableNew: false,
    newButtonConfig: {}
  },
  filters_and_columns_visibility_config: {
    showRefreshButton: true,
    showFiltersButton: true,
    showColumnsVisibilityButton: true,
    showTableGridSwitchButton: true,
    showNewBtn: false,
    newBtnPosition: 'end',
    newClick: null,
    newButtonConfig: {}
  },
  customButtonComponent: {
    component: null
  },
  customFiltersConfig: {
    component: null
  },
  filters_row_end_components: [],
  underFiltersCustomComponents: [],
  rowsSelection: DEFAULT_ROWS_SELECTION_CONFIG,
  gridConfig: {
    component: null
  },
  gridConfigDefaults: {},
  sideTableComponent: {
    component: null
  },
  // Hides the listing itself, leaving the side component as the whole screen. Left
  // undefined means the table is shown, and a side component may still hide it.
  hideTable: undefined,
  globalStyle: {
    tableColClasses: '',
    tableRowClasses: '',
    tableColStyle: {},
    sideTableContentColClasses: ''
  },
  apiConfig: {},
  // What opening the record does, reached by a double click on the row - a single
  // click ticks it, see `rowsSelection.selectOnRowClick`. See `getOpenRecordHandler`.
  rowDblClick: null,
  // The icon that tells the user what a click on a row does - see `getRowClickIndicator`.
  rowClickIndicator: null,
  // Milliseconds the single click waits, so a double click can cancel it. Left unset
  // it is paid only by a table that has a `rowDblClick` to tell apart; 0 never waits.
  rowClickDelay: null,
  onCreate: null,
  onSearch: null,
  onRefreshData: null,
  onDataReceivedWithParams: null,
  backFromTableDetailsView: null,
  tableConfig: {
    styles: DEFAULT_TABLE_STYLE,
    globalConfig: {},
    cellsConfig: {},
    headers: [],
    actionsConfig: [],
    filtersConfig: {
      fields: []
    },
    paginationConfig: DEFAULT_PAGINATION_CONFIG,
    onApplyColumnsVisibility: null
  }
}

const DEFAULT_TABLE_CONFIG = {
  uiView: '',
  key: '',
  title: '',
  showLoader: false,
  callFetchData: false,
  fetchOnCreate: true,
  rememberTableLayout: true,
  visible: undefined,
  permissions: null,
  invalidateSize: false,
  tableData: undefined,
  summaryData: undefined,
  openDetail: null,
  updateHeaders: [],
  internalPagination: {
    enable: false
  },
  title_and_back_config: null,
  showBackButton: null,
  search_and_buttons_config: null,
  filters_and_columns_visibility_config: {
    showRefreshButton: true,
    showFiltersButton: true,
    showColumnsVisibilityButton: true,
    showTableGridSwitchButton: true,
    showNewBtn: false,
    newBtnPosition: 'end',
    newClick: null
  },
  customButtonComponent: null,
  customFiltersConfig: null,
  filters_row_end_components: [],
  underFiltersCustomComponents: [],
  rowsSelection: DEFAULT_ROWS_SELECTION_CONFIG,
  gridConfig: null,
  gridConfigDefaults: {},
  sideTableComponent: null,
  hideTable: undefined,
  globalStyle: {},
  apiConfig: {},
  // What opening the record does, reached by a double click on the row - a single
  // click ticks it, see `rowsSelection.selectOnRowClick`. See `getOpenRecordHandler`.
  rowDblClick: null,
  // The icon that tells the user what a click on a row does - see `getRowClickIndicator`.
  rowClickIndicator: null,
  // Milliseconds the single click waits, so a double click can cancel it. Left unset
  // it is paid only by a table that has a `rowDblClick` to tell apart; 0 never waits.
  rowClickDelay: null,
  onCreate: null,
  onSearch: null,
  onRefreshData: null,
  onDataReceivedWithParams: null,
  backFromTableDetailsView: null,
  tableConfig: {
    styles: DEFAULT_TABLE_STYLE,
    globalConfig: {},
    cellsConfig: {},
    headers: [],
    actionsConfig: [],
    filtersConfig: {
      fields: []
    },
    paginationConfig: DEFAULT_PAGINATION_CONFIG,
    onApplyColumnsVisibility: null
  }
}

const createDefaultBuildConfig = (options = {}) => {
  const {
    paginationConfig = DEFAULT_PAGINATION_CONFIG,
    tableStyle = DEFAULT_TABLE_STYLE
  } = options

  return mergeDeep(DEFAULT_TABLE_CONFIG, {
    tableConfig: {
      styles: tableStyle,
      paginationConfig
    }
  })
}

export function useTableService () {
  /**
   * Builds a `TableGridContent` config with explicit defaults, so the config
   * shape is visible in the editor and callers can override only what they need.
   * For a full reference object shape, see `TABLE_CONFIG_PLACEHOLDERS`.
   *
   * @param {BuildTableConfigOptions} [options={}]
   * @returns {Object}
   */
  const buildConfig = (options = {}) => {
    const defaultBuildConfig = createDefaultBuildConfig(options)
    const { tableConfig = {} } = options
    const config = { ...options }

    delete config.tableStyle
    delete config.paginationConfig
    delete config.tableConfig

    return mergeDeep(defaultBuildConfig, {
      ...config,
      tableConfig
    })
  }

  return {
    buildConfig
  }
}
